import {
  demoModeAtom,
  todosDataAtom,
  userAtom,
  userNotificationsAtom,
} from '@/global-store';
import { api } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

const offlineMode = import.meta.env.VITE_OFFLINE_MODE === 'true';

/**
 * type 4: assignee changed,
 * type 6: comment added,
 * type 8: changed description
 */
const todosNotificationTypes = [4, 6, 8];

/** Notifications only cover the last 30 days. */
const notificationIntervalInDays = 30;

export interface TodoActivity {
  type: number | string;
  modifier: number | string;
  activityDate: number | string;
  props?: {
    assignedTo?: number | string;
    [key: string]: unknown;
  };
}

export interface Todo {
  domain?: string;
  /** Bookmark category the todo was created from, e.g. `manual_test`. */
  category?: string;
  filterId?: string | number;
  assignee?: number | string;
  history?: TodoActivity[];
  jira?: {
    key?: string;
    [key: string]: unknown;
  };
  props?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface TodosData {
  todos: Record<string, Todo>;
  /** Maps a MyGoals board status to the todo ids it contains. */
  boardConfig?: Record<string, number[]>;
  jira?: {
    onPrem?: number;
    /** domain -> Jira project key */
    jiraProjectsMap?: Record<string, string>;
    /** Eye-Able user id -> Jira account id */
    jiraUsersMap?: Record<string, string>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export type TodoNotification = TodoActivity & { todoId: string };

const emptyTodosData: TodosData = { todos: {} };

/**
 * Activities on the current user's todos that are worth notifying about:
 * recent, of a notifiable type, and caused by somebody else.
 *
 * Only the most recent "assignee changed" activity is kept, so a todo that was
 * reassigned several times does not produce one notification per hand-over.
 */
function deriveTodoNotifications(
  todosData: TodosData,
  userId: number | string,
): TodoNotification[] {
  const notifications: TodoNotification[] = [];
  let latestAssigneeNotification: TodoNotification | null = null;

  const notificationInterval = new Date();
  notificationInterval.setDate(
    new Date().getDate() - notificationIntervalInDays,
  );

  Object.keys(todosData.todos).forEach((todoId) => {
    todosData.todos[todoId]?.history?.forEach((activity) => {
      const isAssigneeChange = +activity.type === 4;

      const shouldNotify =
        notificationInterval <= new Date(activity.activityDate) &&
        +(todosData.todos[todoId].assignee ?? NaN) === +userId &&
        todosNotificationTypes.includes(+activity.type) &&
        +activity.modifier !== +userId &&
        // an assignee change is only relevant if it assigned this user
        (!isAssigneeChange || +(activity.props?.assignedTo ?? NaN) === +userId);

      if (!shouldNotify) return;

      if (isAssigneeChange) {
        if (
          !latestAssigneeNotification ||
          latestAssigneeNotification.activityDate < activity.activityDate
        )
          latestAssigneeNotification = { ...activity, todoId };
        return;
      }

      notifications.push({ ...activity, todoId });
    });
  });

  if (latestAssigneeNotification)
    notifications.push(latestAssigneeNotification);

  return notifications;
}

/** In demo mode every todo gets one synthetic notification, so the UI has something to show. */
function deriveDemoTodoNotifications(
  todosData: TodosData,
  userId: number | string,
): TodoNotification[] {
  return Object.keys(todosData.todos).map((todoId, index) => ({
    todoId,
    type: todosNotificationTypes[index % todosNotificationTypes.length],
    modifier: userId,
    activityDate: new Date().getTime(),
  }));
}

/**
 * Loads the organisation's todos file and mirrors it into `todosDataAtom`,
 * together with the notifications derived from it.
 */
export function useFetchTodos() {
  const user = useAtomValue(userAtom);
  const demoMode = useAtomValue(demoModeAtom);
  const setTodosData = useSetAtom(todosDataAtom);
  const setUserNotifications = useSetAtom(userNotificationsAtom);

  const userId = user?.id;

  const query = useQuery({
    enabled: !!userId,
    queryKey: [api.todos, userId],
    queryFn: () => {
      if (offlineMode) return Promise.resolve(emptyTodosData);

      return fetchWrapper.get<TodosData>(api.todos);
    },
  });

  const todosData = query.data;

  useEffect(() => {
    if (!todosData?.todos || !userId) return;

    setTodosData(todosData);
    setUserNotifications(
      demoMode
        ? deriveDemoTodoNotifications(todosData, userId)
        : deriveTodoNotifications(todosData, userId),
    );
  }, [todosData, userId, demoMode, setTodosData, setUserNotifications]);

  return query;
}
